import { useId, useRef, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

export type FilterValues = Record<string, string>;
export type FilterField = { key: string; label: string; emptyLabel?: string; options: { value: string; label: string }[] };

function SearchableFilter({ field, value, onChange }: { field: FilterField; value: string; onChange: (value: string) => void }) {
  const [search, setSearch] = useState("");
  const id = useId();
  const details = useRef<HTMLDetailsElement>(null);
  const choose = (next: string) => { onChange(next); setSearch(""); if (details.current) details.current.open = false; };
  const options = field.options.filter((option) => `${option.label} ${option.value}`.toLowerCase().includes(search.toLowerCase()));
  const selected = field.options.find((option) => option.value === value);
  return <div className="space-y-2">
    <Label htmlFor={`${id}-search`}>{field.label}</Label>
    <details ref={details} className="rounded-md border bg-background">
      <summary className="cursor-pointer px-3 py-2 text-sm">{selected?.label || (value ? value : field.emptyLabel || `All ${field.label.toLowerCase()}`)}</summary>
      <div className="space-y-2 border-t p-2">
        <Input id={`${id}-search`} aria-label={`Search ${field.label.toLowerCase()} options`} placeholder={`Search ${field.label.toLowerCase()}`} value={search} onChange={(event) => setSearch(event.target.value)} />
        <div className="max-h-48 overflow-y-auto" role="group" aria-label={field.label}>
          <Button type="button" className="w-full justify-start" variant={!value ? "secondary" : "ghost"} onClick={() => choose("")}>{field.emptyLabel || `All ${field.label.toLowerCase()}`}</Button>
          {options.map((option) => <Button type="button" key={option.value} className="w-full justify-start" variant={value === option.value ? "secondary" : "ghost"} onClick={() => choose(option.value)}>{option.label}</Button>)}
          {!options.length && <p className="p-2 text-sm text-muted-foreground">No matches</p>}
        </div>
      </div>
    </details>
  </div>;
}

export function AdvancedOptions({ fields, values, onChange, inline = false }: { fields: FilterField[]; values: FilterValues; onChange: (values: FilterValues) => void; inline?: boolean }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(values);
  const active = Object.values(values).filter(Boolean).length;
  const controls = (current: FilterValues, update: (values: FilterValues) => void) => <div className="space-y-4">{fields.map((field) => <SearchableFilter key={field.key} field={field} value={current[field.key] || ""} onChange={(value) => update({ ...current, [field.key]: value })} />)}</div>;
  if (inline) return controls(values, onChange);
  return <Dialog open={open} onOpenChange={setOpen}>
    <Button type="button" variant="outline" onClick={() => { setDraft(values); setOpen(true); }}><SlidersHorizontal className="mr-2 size-4" />Advanced options{active > 0 ? ` (${active})` : ""}</Button>
    <DialogContent><DialogHeader><DialogTitle>Advanced options</DialogTitle><DialogDescription>Narrow this search by organization and related accounts.</DialogDescription></DialogHeader>
      {controls(draft, setDraft)}
      <DialogFooter><Button type="button" variant="ghost" onClick={() => setDraft({})}>Clear filters</Button><Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button type="button" onClick={() => { onChange(draft); setOpen(false); }}>Apply filters</Button></DialogFooter>
    </DialogContent>
  </Dialog>;
}
